import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JobService from '../services/JobService';
import ProformaService from '../services/ProformaService';
import ClientService from '../services/ClientService';
import CompanyService from '../services/CompanyService';
import { useMoneyLock } from '../contexts/MoneyContext';
import { fmtDate } from '../utils/format';
import { useCompanyLogo } from '../components/common/companyLogo';
import { Edit, ItemsTable, Letterhead, Sheet } from '../components/print/DocumentSheet';

/** A4 pro-forma invoice of a job to print or save as PDF (browser print); every value can be corrected on the sheet. */
export default function ProformaPrintPage() {
    const { id, proformaId } = useParams();
    const { t } = useTranslation();
    const logo = useCompanyLogo();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);
    const money = useMoneyLock(`job:${id}`);

    useEffect(() => {
        if (!money.visible) return;
        Promise.all([ProformaService.get(id, proformaId), JobService.get(id), CompanyService.get()])
            .then(([proforma, job, company]) => ClientService.get(job.client.id).then(client => setData({ proforma, job, company, client })))
            .catch(() => setError(true));
    }, [id, proformaId, money.visible]);

    const toolbar = (
        <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', maxWidth: '210mm', mx: 'auto', mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/jobs/${id}`)} sx={{ color: '#64748B' }}>{t('common.back')}</Button>
            <Typography sx={{ flex: 1, fontSize: 13, color: '#64748B', minWidth: 200 }}>{data ? t('print.editHint') : ''}</Typography>
            <Button variant="contained" startIcon={<PrintOutlinedIcon />} disabled={!data || !money.visible} onClick={() => window.print()}>{t('print.print')}</Button>
        </Box>
    );

    if (!money.visible) {
        return (
            <Box sx={{ p: 3 }}>
                {toolbar}
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                    <LockOutlinedIcon sx={{ color: '#CBD5E1', fontSize: 40 }} />
                    <Typography sx={{ color: '#64748B', mt: 1, mb: 2 }}>{t('money.printLocked')}</Typography>
                    <Button variant="contained" onClick={money.show} disableElevation>{t('money.show')}</Button>
                </Box>
            </Box>
        );
    }

    if (error || !data) {
        return (
            <Box sx={{ p: 3 }}>
                {toolbar}
                <Box sx={{ textAlign: 'center', mt: 8 }}>
                    {error ? <Typography sx={{ color: '#64748B' }}>{t('proforma.notFound')}</Typography> : <CircularProgress />}
                </Box>
            </Box>
        );
    }

    const { proforma, job, company, client } = data;
    const subject = [job.number, job.title].filter(Boolean).join(' – ');
    const location = [
        job.municipality && `${t('jobs.fields.municipality')}: ${job.municipality}`,
        job.cadastralZone && `${t('jobs.fields.cadastralZone')}: ${job.cadastralZone}`,
        job.parcelNumber && `${t('jobs.fields.parcelNumber')}: ${job.parcelNumber}`,
    ].filter(Boolean).join(' · ');
    const clientAddress = [client.address, client.city].filter(Boolean).join(', ');
    const clientContact = [client.phone, client.email].filter(Boolean).join(' · ');
    const bank = [company.bankAccount, company.bankName].filter(Boolean).join(', ');

    return (
        <Sheet toolbar={toolbar}>
            <Letterhead company={company} logo={logo} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 4, flexWrap: 'wrap', mb: 5 }}>
                <Box>
                    <Typography sx={{ fontSize: 11.5, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>{t('proforma.printBuyer')}</Typography>
                    <Edit block sx={{ fontSize: 15, fontWeight: 700 }}>{client.name}</Edit>
                    {client.idNumber && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{client.idNumber}</Edit>}
                    {clientAddress && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{clientAddress}</Edit>}
                    {clientContact && <Edit block sx={{ fontSize: 12.5, color: '#4B5563' }}>{clientContact}</Edit>}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Edit block sx={{ fontSize: 22, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t('proforma.printTitle')}</Edit>
                    <Edit block sx={{ fontSize: 13, color: '#4B5563' }}>{`${t('quotes.printNo')}: ${proforma.number}`}</Edit>
                    <Edit block sx={{ fontSize: 13, color: '#4B5563' }}>{`${t('proforma.issueDate')}: ${fmtDate(proforma.issueDate)}`}</Edit>
                    {proforma.dueDate && <Edit block sx={{ fontSize: 13, color: '#4B5563' }}>{`${t('proforma.dueDate')}: ${fmtDate(proforma.dueDate)}`}</Edit>}
                </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Edit block sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>{subject}</Edit>
                {location && <Edit block sx={{ fontSize: 13, color: '#374151' }}>{location}</Edit>}
            </Box>

            <ItemsTable doc={proforma} />

            {proforma.note && <Edit block sx={{ fontSize: 13, lineHeight: 1.7, color: '#374151', mb: 2 }}>{proforma.note}</Edit>}
            {/* Payment details: the firm's bank account (Company page) and the pro-forma number as reference. */}
            <Box sx={{ fontSize: 13, lineHeight: 1.7, color: '#374151', mb: 8 }}>
                {bank && <Edit block>{t('proforma.printPayTo', { account: bank })}</Edit>}
                <Edit block>{t('proforma.printReference', { number: proforma.number })}</Edit>
                {!proforma.vatEnabled && <Edit block>{t('proforma.printNoVat')}</Edit>}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ width: 240 }}>
                    <Box sx={{ borderBottom: '1px solid #111827', height: 56 }} />
                    <Typography sx={{ fontSize: 12, color: '#4B5563', mt: 0.75, textAlign: 'center' }}>{t('proforma.printSign')}</Typography>
                </Box>
            </Box>
        </Sheet>
    );
}
